import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, BookOpen, FileText, GraduationCap, School, Award, Lock } from 'lucide-react';

const PracticeLibraryLanding = ({ progress }) => {
  const navigate = useNavigate();
  
  // Get user info
  const user = progress?.user;
  const userYearGroup = user?.studentProfile?.yearGroup || user?.yearGroup || null;
  const userType = user?.userType;
  
  // Check if user is admin or superuser
  const isAdminOrSuper = useMemo(() => {
    if (!user) return false;
    return userType === 'admin' || 
           userType === 'superuser' || 
           user?.isAdmin === true ||
           user?.isSuperUser === true ||
           user?.email?.includes('admin@');
  }, [user, userType]);

  // Map user's year group to allowed categories
  const getAllowedCategories = useMemo(() => {
    if (isAdminOrSuper) return null; // null means all categories allowed
    
    if (!userYearGroup) return null; // If no year group, show all (guest/incomplete profile)
    
    const yearLower = userYearGroup.toLowerCase().replace(/\s+/g, '-');
    
    // Map year groups to category IDs
    const yearToCategoryMap = {
      'year-1': ['seven-plus'],
      'year-2': ['seven-plus'],
      'year 1': ['seven-plus'],
      'year 2': ['seven-plus'],
      'year-3': ['eleven-plus'],
      'year-4': ['eleven-plus'],
      'year 3': ['eleven-plus'],
      'year 4': ['eleven-plus'],
      'year-5': ['eleven-plus'],
      'year-6': ['eleven-plus'],
      'year 5': ['eleven-plus'],
      'year 6': ['eleven-plus'],
      '7plus': ['seven-plus'],
      '7+': ['seven-plus'],
      '11plus': ['eleven-plus'],
      '11+': ['eleven-plus'],
      'year-7': ['year-9'],
      'year-8': ['year-9'],
      'year-9': ['year-9'],
      'year 7': ['year-9'],
      'year 8': ['year-9'],
      'year 9': ['year-9'],
      'year-10': ['gcse'],
      'year-11': ['gcse'],
      'year 10': ['gcse'],
      'year 11': ['gcse'],
      'gcse': ['gcse'],
      'year-12': ['a-levels'],
      'year-13': ['a-levels'],
      'year 12': ['a-levels'],
      'year 13': ['a-levels'],
      'a-level': ['a-levels'],
      'a-levels': ['a-levels']
    };
    
    return yearToCategoryMap[yearLower] || yearToCategoryMap[userYearGroup] || null;
  }, [userYearGroup, isAdminOrSuper]);

  const allCategories = [
    {
      id: 'seven-plus',
      title: '7+ Entry',
      description: 'Independent school preparation for Year 2',
      icon: School,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-500/10',
      subjects: [
        { id: 'maths', name: 'Mathematics', papers: 15 },
        { id: 'english', name: 'English', papers: 15 },
        { id: 'reasoning', name: 'Reasoning', papers: 10 }
      ]
    },
    {
      id: 'eleven-plus',
      title: '11+ Entry',
      description: 'Grammar school and independent school exams',
      icon: GraduationCap,
      color: 'from-violet-500 to-purple-500',
      bgColor: 'bg-violet-500/10',
      subjects: [
        { id: 'maths-gl', name: 'Maths (GL)', papers: 47 },
        { id: 'maths-sutton', name: 'Maths (Sutton)', papers: 47 },
        { id: 'english-gl', name: 'English (GL)', papers: 20 },
        { id: 'verbal-reasoning', name: 'Verbal Reasoning', papers: 30 },
        { id: 'non-verbal', name: 'Non-Verbal Reasoning', papers: 25 }
      ]
    },
    {
      id: 'year-9',
      title: 'Year 9 (KS3)',
      description: 'Key Stage 3 practice papers',
      icon: BookOpen,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/10',
      subjects: [
        { id: 'maths', name: 'Mathematics', papers: 20 },
        { id: 'english', name: 'English', papers: 15 },
        { id: 'science', name: 'Science', papers: 25 }
      ]
    },
    {
      id: 'gcse',
      title: 'GCSE',
      description: 'GCSE exam preparation',
      icon: Award,
      color: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-500/10',
      subjects: [
        { id: 'maths-aqa', name: 'Maths (AQA)', papers: 30 },
        { id: 'maths-edexcel', name: 'Maths (Edexcel)', papers: 30 },
        { id: 'english-lang', name: 'English Language', papers: 20 },
        { id: 'english-lit', name: 'English Literature', papers: 20 },
        { id: 'science', name: 'Combined Science', papers: 40 }
      ]
    },
    {
      id: 'a-levels',
      title: 'A-Levels',
      description: 'A-Level exam preparation',
      icon: GraduationCap,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-500/10',
      subjects: [
        { id: 'maths', name: 'Mathematics', papers: 25 },
        { id: 'further-maths', name: 'Further Maths', papers: 20 },
        { id: 'physics', name: 'Physics', papers: 20 },
        { id: 'chemistry', name: 'Chemistry', papers: 20 }
      ]
    }
  ];

  // Filter categories based on user's access
  const categories = useMemo(() => {
    if (!getAllowedCategories) return allCategories; // Admin or no restriction
    
    return allCategories.map(cat => ({
      ...cat,
      isLocked: !getAllowedCategories.includes(cat.id)
    }));
  }, [getAllowedCategories]);

  // Get the user's primary category for display
  const userCategoryLabel = useMemo(() => {
    if (isAdminOrSuper) return 'Full Access';
    if (!getAllowedCategories) return null;
    
    const primaryCategory = allCategories.find(c => getAllowedCategories.includes(c.id));
    return primaryCategory?.title || userYearGroup;
  }, [getAllowedCategories, isAdminOrSuper, userYearGroup]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/20"
              data-testid="back-btn"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            
            {userCategoryLabel && (
              <Badge className="bg-white/20 text-white border-white/30">
                <GraduationCap className="w-3 h-3 mr-1" />
                {userCategoryLabel}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <FileText className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Practice Test Library</h1>
              <p className="text-white/80 mt-1">
                {isAdminOrSuper 
                  ? 'Full access to all practice papers across all year groups'
                  : userYearGroup 
                    ? `Practice papers for ${userYearGroup}`
                    : 'Access practice papers for your year group'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid gap-6">
          {categories.map((category) => {
            const isLocked = category.isLocked;
            
            return (
              <Card 
                key={category.id} 
                className={`border-slate-700 overflow-hidden ${
                  isLocked ? 'bg-slate-800/30 opacity-60' : 'bg-slate-800/50'
                }`}
                data-testid={`category-${category.id}`}
              >
                <CardHeader className={`bg-gradient-to-r ${category.color} text-white ${isLocked ? 'opacity-70' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <category.icon className="h-6 w-6" />
                      <div>
                        <CardTitle className="text-xl flex items-center gap-2">
                          {category.title}
                          {isLocked && <Lock className="w-4 h-4" />}
                        </CardTitle>
                        <CardDescription className="text-white/80">{category.description}</CardDescription>
                      </div>
                    </div>
                    {isLocked && (
                      <Badge className="bg-black/20 text-white/80">
                        Not in your plan
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {isLocked ? (
                    <div className="text-center py-4">
                      <p className="text-slate-400 text-sm">
                        This content is for a different year group. Contact support to change your plan.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {category.subjects.map((subject) => (
                        <Button
                          key={subject.id}
                          variant="outline"
                          className="h-auto py-3 px-4 flex flex-col items-start justify-start border-slate-600 hover:bg-slate-700 hover:border-slate-500 text-left"
                          onClick={() => navigate(`/papers/${category.id}/${subject.id}`)}
                          data-testid={`subject-${category.id}-${subject.id}`}
                        >
                          <span className="font-medium text-white">{subject.name}</span>
                          <span className="text-xs text-slate-400">{subject.papers} papers</span>
                        </Button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-400">500+</div>
              <div className="text-sm text-slate-400">Practice Papers</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-purple-400">5</div>
              <div className="text-sm text-slate-400">Exam Levels</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-400">20+</div>
              <div className="text-sm text-slate-400">Subjects</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-amber-400">100%</div>
              <div className="text-sm text-slate-400">With Solutions</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PracticeLibraryLanding;
